import type { DetailedStock } from "@/shared/types/integrations/BrapiDev/DetailedStock";
import type { SortBy } from "@/shared/types/integrations/BrapiDev/SortBy";
import type { SortOrder } from "@/shared/types/integrations/BrapiDev/SortOrder";
import type { Stock } from "@/shared/types/integrations/BrapiDev/Stock";
import type { ValidIntervals } from "@/shared/types/integrations/BrapiDev/ValidIntervals";
import type { ValidRanges } from "@/shared/types/integrations/BrapiDev/ValidRanges";
import { bus } from "@/shared/events/integrations/brapiDevEventEmitter";
import { get } from "@/shared/config/server/HttpClient";
import requestResponseTrait from "@/shared/config/error/requestResponseTrait";
import sanitize from "@/shared/utils/sanitize";
import type { ResponseTraitError } from "@/types/Error/Error";

/**
 * @description Handles the request to get the list of currency quotes.
 *
 * @param {number} limit - The limit number of cryptocurrencies to return
 * @param {number} page - The page number of cryptocurrencies to return
 * @param {SortBy} sortBy - The field to sort by
 * @param {SortOrder} sortOrder - The order to sort by
 * @param {string} search - The search query
 * @returns Promise<Stock[] | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function listStocks(
	limit = 12,
	page = 1,
	sortBy?: SortBy,
	sortOrder?: SortOrder,
	search?: string,
): Promise<Stock[] | ResponseTraitError> {
	let url: string;

	const searchQuery = search ? `search=${search}&` : "";

	if (sortBy && sortOrder) {
		url = `/proxy/stocks?${searchQuery}limit=${limit}&page=${page}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
	} else {
		url = `/proxy/stocks?${searchQuery}limit=${limit}&page=${page}`;
	}

	try {
		const response = await get(url);

		await requestResponseTrait(response);

		const stocks: Stock[] = await sanitize(await response.json());

		if (!sortBy && !sortOrder) {
			bus.emit("getCurrencyStocks", { stocks });
		} else {
			bus.emit("getHomeCurrencyStocks", { stocks });
		}

		return stocks;
	} catch (error) {
		return error as ResponseTraitError;
	}
}

/**
 * @description Handles the request to get the currency quotes.
 *
 * @param {string} ticker - The ticker of the currency
 * @param {ValidRanges} range - The range of the currency quotes
 * @param {ValidIntervals} interval - The interval of the currency quotes
 * @returns Promise<DetailedStock | ResponseTraitError>
 * @throws {Error} If the request to the proxy fails
 */
export async function stockDetail(
	ticker: string,
	range: ValidRanges = "3mo",
	interval: ValidIntervals = "1d",
): Promise<DetailedStock | ResponseTraitError> {
	const url = `/proxy/stocks/${ticker}?range=${range}&interval=${interval}&modules=summaryProfile`;
	try {
		const response = await get(url);

		await requestResponseTrait(response);

		const detailedStock: DetailedStock = await sanitize(await response.json());

		const { default: translate } = await import("@/shared/utils/externalDataTranslator");

		if (detailedStock.historicalDataPrice.length > 0) {
			if (detailedStock.summaryProfile?.longBusinessSummary) {
				detailedStock.summaryProfile.longBusinessSummary = await translate(
					detailedStock.summaryProfile.longBusinessSummary,
				);
			}

			if (detailedStock.summaryProfile?.sector) {
				detailedStock.summaryProfile.sector = await translate(
					detailedStock.summaryProfile.sector,
				);
			}

			if (detailedStock.summaryProfile?.industry) {
				detailedStock.summaryProfile.industry = await translate(
					detailedStock.summaryProfile.industry,
				);
			}

			bus.emit("getDetailedStock", { detailedStock });
		}

		return detailedStock;
	} catch (error) {
		return error as ResponseTraitError;
	}
}
